import { makeStyles } from '@mui/styles';
import { Avatar, Box, Tooltip } from 'apollo-react';
import NavigationBar from 'apollo-react/components/NavigationBar';
import Globe from 'apollo-react-icons/Globe';
import PlusCircle from 'apollo-react-icons/PlusCircle';
import StatusNegative from 'apollo-react-icons/StatusNegative';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ArchiveModeSwitcher } from './ArchivedModeSwitcher';
import Notifications from './Notifications/Notifications';
import { getCustomerLogoImage } from '../../actions/CustomerLogoActions';
import { changeDashboardEditingState } from '../../actions/DashboardConfigActions';
import { fetchAppNavigationFromServer } from '../../actions/NavigationActions';
import { fetchCreatePatFromServer, fetchRevokePatFromServer } from '../../actions/PatActions';
import { sidenavIsSubMenu } from '../../actions/SidenavActions';
import IQVIALogo from '../../assets/icons/iqvia_core.svg';
import HelpDropdown from '../../components/PilgrimTopMenu/HelpDropdown';
import { PILGRIM_TOP_MENU_HEIGHT } from '../../constants/SizeConstants';
import { HOME } from '../../constants/Urls';
import ChangeLanguageDialog from '../../containers/Dashboard/components/ChangeLanguageDialog';
import FormHistoryDialog from '../../containers/FormHistoryDialog';
import HelpTopicsDialog from '../../containers/HelpTopicsDialog';
import InstalledVersionsDialog from '../../containers/InstalledVersionsDialog';
import PatDialog from '../../containers/PatDialog';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getMode } from '../../selectors/DashboardSelectors';
import { getAvailableNavigation } from '../../selectors/MasterSetupPageSelectors';
import { getUITA } from '../../utils/commonUtils';
import SearchBar from '../PilgrimTopMenu/EnhancedSearchBar';

const useStyles = makeStyles({
  navigationBar: {
    '& > header > div > div:nth-of-type(2)': {
      display: 'none',
    },
    '& > header > div > div:nth-of-type(3)': {
      width: '100%',
    },
    '& > header > div > div:nth-of-type(3) > button': {
      overflow: 'visible',
    },
  },
});

const PilgrimTopMenu = () => {
  const classes = useStyles();
  const translateString = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isExternalUser, isArchiveUser } = useSelector((state) => state.auth);

  const customerLogo = useSelector((state) => state.customerLogo?.customerLogoImage);

  const mode = useSelector(getMode);

  const navigation = useSelector(getAvailableNavigation, shallowEqual);

  const username = localStorage.getItem('userFullName') || '';
  const email = localStorage.getItem('actorCodeWithDomain');

  const initials = username
    .split(' ')
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  const menuItems = [
    {
      text: translateString('Change Language'),
      icon: Globe,
      onClick: () => dispatch(changeDashboardEditingState('changeLanguage')),
    },
    !isExternalUser && {
      text: translateString('Create PAT'),
      icon: PlusCircle,
      onClick: () => dispatch(fetchCreatePatFromServer()),
    },
    !isExternalUser && {
      text: translateString('Revoke PAT'),
      icon: StatusNegative,
      onClick: () => dispatch(fetchRevokePatFromServer()),
    },
  ].filter(Boolean);

  const profileMenuProps = {
    title: username,
    email,
    logoutText: translateString('Log out'),
    icon: (
      <Tooltip title={translateString('Account Actions')} placement="bottom-start">
        <div
          style={{
            paddingLeft: '16px',
            paddingRight: '16px',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Avatar stateless={true} alt={username} size="small">
            {initials}
          </Avatar>
        </div>
      </Tooltip>
    ),
    logoutButtonProps: {
      variant: 'secondary',
      onClick: () => window.logout(),
    },
    menuItems,
  };

  const logoProps = {
    src: IQVIALogo,
    alt: 'IQVIA Logo',
    onClick: () => {
      history.push(HOME);
      dispatch(sidenavIsSubMenu(false));
    },
  };

  useEffect(() => {
    if (!navigation.length) {
      dispatch(fetchAppNavigationFromServer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fetchAppNavigationFromServer, navigation]);

  useEffect(() => {
    dispatch(getCustomerLogoImage());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.navigationBar} ui-ta={getUITA('MenuDropDown')}>
      <NavigationBar
        sx={{
          '& > div> div:last-child > button': {
            padding: 0,
            height: '100%',
          },
        }}
        aria-label={translateString('Account Actions')}
        profileMenuProps={profileMenuProps}
        variant="brand"
        logoProps={logoProps}
        otherButtons={
          <Box
            display="flex"
            alignItems="center"
            style={{ flex: 1, justifyContent: customerLogo ? 'space-between' : 'end' }}
          >
            {customerLogo && (
              <img
                src={customerLogo}
                alt={translateString('Customer Logo')}
                style={{ maxHeight: `${PILGRIM_TOP_MENU_HEIGHT - 6}px` }}
              />
            )}
            <Box display="flex" alignItems="center" gap={3}>
              {isArchiveUser && <ArchiveModeSwitcher />}
              {!isExternalUser && <SearchBar />}
              <Box display="flex" alignItems="center">
                {!isExternalUser && <Notifications />}
                <HelpDropdown />
              </Box>
            </Box>
          </Box>
        }
      />
      <PatDialog />
      <InstalledVersionsDialog />
      <FormHistoryDialog />
      <HelpTopicsDialog />
      {mode === 'changeLanguage' && <ChangeLanguageDialog />}
    </div>
  );
};

export default PilgrimTopMenu;
